/**
 * Logging Pipeline Observer
 *
 * Implements IPipelineObserver by translating all pipeline events into log entries.
 * Single responsibility: event → LogEntry translation.
 * Uses LoggingService as the data store.
 */

import { IPipelineObserver } from './IPipelineObserver';
import type {
  PipelineStartEvent,
  HookBeforeEvent,
  HookAfterEvent,
  SubscriberQueuedEvent,
  SubscriberDeliveredEvent,
  SubscriberFailedEvent,
  PipelineEndEvent,
} from './PipelineObserverEvents';
import { getLoggingService, getLogSettings } from '../logging';
import { createModuleLogger } from '../utils';
import type { HookLogEntry, SubscriberLogEntry } from '../logging/LogEntry';

const logger = createModuleLogger('LoggingPipelineObserver');

/**
 * Observer that translates pipeline events into log entries.
 *
 * Stores input snapshots temporarily (onHookBefore → onHookAfter pattern).
 * Thread-safe using Map keyed by messageId.
 */
export class LoggingPipelineObserver implements IPipelineObserver {
  // Temporarily store hook input snapshots until onHookAfter
  // Key: "${messageId}:${hookName}"
  private hookInputSnapshots = new Map<string, HookBeforeEvent>();

  constructor() {}

  onPipelineStart(event: PipelineStartEvent): void {
    if (!getLogSettings().isHookLoggingEnabled()) {
      return;
    }

    try {
      const loggingService = getLoggingService();

      // Create new message activity log
      const log = loggingService.createMessageLog(
        event.messageId,
        event.serverId,
        event.topic,
        event.messageType,
      );

      // Set protocol and direction
      log.protocol = event.protocol;
      log.direction = event.direction;

      // Set pipeline start info
      log.pipelineStart = {
        timestamp: event.timestamp,
        envelopeSnapshot: event.envelopeSnapshot,
      };

      // Set pipeline info
      log.pipelineInfo = {
        pipelineName: event.pipelineName,
        hookSequence: event.hookSequence,
        transport: event.transport,
        protocol: event.protocol,
        direction: event.direction,
      };
    } catch (error) {
      logger.warn(`[LoggingPipelineObserver.onPipelineStart] Error: ${error}`);
    }
  }

  onHookBefore(event: HookBeforeEvent): void {
    if (!getLogSettings().isHookLoggingEnabled()) {
      return;
    }

    try {
      // Store input snapshot for later retrieval in onHookAfter
      const key = `${event.messageId}:${event.hookName}`;
      this.hookInputSnapshots.set(key, event);
    } catch (error) {
      logger.warn(`[LoggingPipelineObserver.onHookBefore] Error: ${error}`);
    }
  }

  onHookAfter(event: HookAfterEvent): void {
    if (!getLogSettings().isHookLoggingEnabled()) {
      return;
    }

    try {
      const loggingService = getLoggingService();

      // Retrieve input snapshot from temporary storage
      const key = `${event.messageId}:${event.hookName}`;
      const beforeEvent = this.hookInputSnapshots.get(key);
      this.hookInputSnapshots.delete(key); // Clean up

      // Build hook log entry
      const hookLog: HookLogEntry = {
        hookName: event.hookName,
        priority: event.priority,
        status: event.status === 'success' ? 'success' : event.status === 'aborted' ? 'aborted' : 'error',
        message: event.error ? `Hook failed: ${event.error.message}` : 'Hook completed',
        executionTime: event.executionTime,
        timestamp: event.timestamp,
        activityLog: event.activityLog,
        inputEnvelopeSnapshot: beforeEvent?.envelopeSnapshot,
        outputEnvelopeSnapshot: event.envelopeSnapshot,
        dataChanged: event.dataChanged,
      };

      if (event.error) {
        hookLog.error = {
          message: event.error.message,
          stack: event.error.stack,
        };
      }

      // Log the hook execution
      loggingService.logHookExecution(event.messageId, hookLog);
    } catch (error) {
      logger.warn(`[LoggingPipelineObserver.onHookAfter] Error: ${error}`);
    }
  }

  onSubscriberQueued(event: SubscriberQueuedEvent): void {
    if (!getLogSettings().isSubscriberLoggingEnabled()) {
      return;
    }

    try {
      const loggingService = getLoggingService();

      const subLog: SubscriberLogEntry = {
        subscriberId: event.subscriberId,
        topic: event.topic,
        status: 'queued',
        message: `Queuing subscriber delivery`,
        timestamp: event.timestamp,
        inputEnvelopeSnapshot: event.envelopeSnapshot,
      };

      loggingService.logSubscriberDelivery(event.messageId, subLog);
    } catch (error) {
      logger.warn(`[LoggingPipelineObserver.onSubscriberQueued] Error: ${error}`);
    }
  }

  onSubscriberDelivered(event: SubscriberDeliveredEvent): void {
    if (!getLogSettings().isSubscriberLoggingEnabled()) {
      return;
    }

    try {
      const loggingService = getLoggingService();

      const subLog: SubscriberLogEntry = {
        subscriberId: event.subscriberId,
        topic: event.topic,
        status: 'delivered',
        message: `✅ Subscriber notified`,
        processingTime: event.processingTime,
        timestamp: event.timestamp,
        callbackInfo: {
          functionName: event.callbackName,
          duration: event.processingTime,
        },
      };

      loggingService.logSubscriberDelivery(event.messageId, subLog);
    } catch (error) {
      logger.warn(`[LoggingPipelineObserver.onSubscriberDelivered] Error: ${error}`);
    }
  }

  onSubscriberFailed(event: SubscriberFailedEvent): void {
    if (!getLogSettings().isSubscriberLoggingEnabled()) {
      return;
    }

    try {
      const loggingService = getLoggingService();

      const subLog: SubscriberLogEntry = {
        subscriberId: event.subscriberId,
        topic: event.topic,
        status: 'error',
        message: `❌ Subscriber failed: ${event.error.message}`,
        processingTime: event.processingTime,
        timestamp: event.timestamp,
        error: {
          message: event.error.message,
          stack: event.error.stack,
        },
      };

      loggingService.logSubscriberDelivery(event.messageId, subLog);
    } catch (error) {
      logger.warn(`[LoggingPipelineObserver.onSubscriberFailed] Error: ${error}`);
    }
  }

  onPipelineEnd(event: PipelineEndEvent): void {
    if (!getLogSettings().isHookLoggingEnabled()) {
      return;
    }

    try {
      const loggingService = getLoggingService();

      // Retrieve the message log
      const log = loggingService.getMessageLog(event.messageId);
      if (!log) {
        logger.warn(`[LoggingPipelineObserver.onPipelineEnd] No log found for ${event.messageId}`);
        return;
      }

      // Update pipeline end info
      log.pipelineEnd = {
        timestamp: event.timestamp,
        envelopeSnapshot: event.envelopeSnapshot,
        status: event.status,
      };

      // Complete the message log
      loggingService.completeMessageLog(event.messageId, event.status, {
        totalTime: event.totalTime,
      });
    } catch (error) {
      logger.warn(`[LoggingPipelineObserver.onPipelineEnd] Error: ${error}`);
    }
  }
}
